/**
 * @element qds-data-table
 *
 * Tabular data component that composes qds-column-header, qds-checkbox,
 * qds-row-actions, qds-pagination, and qds-empty-state.
 *
 * Renders a real `<table>` in the shadow root — not a grid of divs — so native
 * table layout, colspan, and screen-reader navigation work correctly.
 *
 * ## Client vs server mode
 *
 * `sort-mode="client"`: the table sorts `rows` itself and re-renders.
 * `sort-mode="server"`: the table fires `qds-sort-change` and renders whatever
 * `rows` it is next given, in the given order. Same split for pagination.
 * Never mix modes — a table that sorts the current page client-side while the
 * server holds the rest produces an order that is wrong in a way users cannot
 * see.
 *
 * ## Page-scoped select-all
 *
 * Select-all applies to the **current page only**. A select-all that silently
 * spans 1,284 unloaded rows followed by a delete is a data-loss incident.
 *
 * ## textContent-only cell rule
 *
 * Never `innerHTML` a cell value. Table data comes from an API; a name field
 * containing markup must render as text. Use `textContent`, the built-in
 * `link` type, or a `Node` the consumer built themselves via the column
 * `render` function.
 *
 * ## Sticky-column width requirement
 *
 * Sticky columns must declare a `width`. The table computes `left`/`right`
 * offsets from preceding sticky columns' widths; without a width the offset
 * cannot be calculated.
 *
 * ## 2,000-row ceiling
 *
 * v1 renders every row. At 40px rows, ~2,000 rows is where scrolling stutters
 * on a mid-range laptop. Paginate at 100 for larger datasets. Windowing is out
 * of scope.
 *
 * @attr {('sm'|'md'|'lg')} density - Row density. Default "md".
 * @attr {boolean} striped - Zebra striping. Default false.
 * @attr {boolean} sticky-header - Sticky table header. Default false.
 * @attr {boolean} selectable - Leading checkbox column. Default false.
 * @attr {('multiple'|'single')} selection-mode - Selection behaviour. Default "multiple".
 * @attr {string} selected-ids - Comma-separated selected row ids.
 * @attr {('client'|'server')} sort-mode - Sort mode. Default "client".
 * @attr {boolean} paginate - Renders pagination footer. Default false.
 * @attr {number} page - Current page (1-based). Default 1.
 * @attr {number} page-size - Rows per page. Default 25.
 * @attr {number} total - Total row count. Defaults to rows.length.
 * @attr {('client'|'server')} pagination-mode - Pagination mode. Default "client".
 * @attr {string} row-id-key - Field that identifies a row. Default "id".
 * @attr {string} row-label-key - Field for accessible names. Defaults to first text column.
 * @attr {string} empty-title - Title for the default empty state.
 * @attr {string} empty-description - Description for the default empty state.
 * @attr {boolean} loading - Renders skeleton rows. Default false.
 * @attr {string} caption - Visually hidden table caption.
 *
 * @property {QdsColumnDef[]} columns - Column definitions. Property only.
 * @property {QdsTableRow[]} rows - Row data. Property only.
 * @property {QdsRowAction[]} actions - Row action definitions. Property only.
 * @property {QdsSortState[]} sort - Sort state, multi-column in priority order.
 * @property {string[]} selectedIds - Selected row ids.
 *
 * @csspart container - Outer wrapper.
 * @csspart toolbar - Toolbar slot wrapper.
 * @csspart scroll - Scroll container.
 * @csspart table - The table element.
 * @csspart thead - Table header.
 * @csspart header-row - Header row.
 * @csspart header-cell - Header cell (th).
 * @csspart tbody - Table body.
 * @csspart row - Body row.
 * @csspart row-selected - Added to selected rows.
 * @csspart row-focused - Added to focused row.
 * @csspart cell - Body cell.
 * @csspart cell-numeric - Added to numeric cells.
 * @csspart select-cell - Selection checkbox cell.
 * @csspart actions-cell - Row actions cell.
 * @csspart footer - Footer wrapper.
 * @csspart pagination - Pagination component.
 * @csspart empty - Empty state row.
 * @csspart skeleton-row - Skeleton loading row.
 *
 * @fires qds-sort-change - Sort changed. detail: {component, sort: QdsSortState[]}
 * @fires qds-selection-change - Selection changed. detail: QdsSelectionChangeEventDetail
 * @fires qds-row-click - Row body clicked or Enter on focused row. detail: {component, rowId, row}
 * @fires qds-row-action - Relayed from qds-row-actions with row populated. detail: QdsRowActionEventDetail
 * @fires qds-page-change - Relayed from qds-pagination. detail: QdsPageChangeEventDetail
 * @fires qds-column-resize - Column resize committed. detail: {component, key, width}
 *
 * @slot empty - Replaces the default empty state.
 * @slot toolbar - Above the header — filters, search, bulk actions.
 * @slot footer - Below pagination.
 *
 * @example
 * <qds-data-table id="positions" density="md" selectable sticky-header></qds-data-table>
 * <script type="module">
 *   const t = document.getElementById('positions');
 *   t.columns = [
 *     { key: 'symbol', label: 'Symbol', sortable: true, sticky: 'start', width: '120px' },
 *     { key: 'name', label: 'Name', sortable: true, minWidth: '200px' },
 *     { key: 'website', label: 'Website', type: 'link', textKey: 'name', target: '_blank' },
 *     { key: 'marketValue', label: 'Market value', type: 'currency', sortable: true }
 *   ];
 *   t.rows = await loadPositions();
 *   t.actions = [
 *     { id: 'view', label: 'View detail', icon: 'pi-external-link' },
 *     { id: 'delete', label: 'Remove position', destructive: true }
 *   ];
 * </script>
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { sanitizeUrl } from '../shared/url-utils.js';
import type {
  QdsColumnDef,
  QdsTableRow,
  QdsSortState,
  QdsSortDirection,
  QdsRowAction,
  QdsSelectionChangeEventDetail,
  QdsRowActionEventDetail,
  QdsPageChangeEventDetail,
  ComponentEventDetail,
} from '../../types/index.js';

type QdsDensity = 'sm' | 'md' | 'lg';
type QdsSelectionMode = 'multiple' | 'single';
type QdsSortMode = 'client' | 'server';
type QdsPaginationMode = 'client' | 'server';

interface QdsSortEventDetail {
  component: HTMLElement;
  key: string;
  direction: QdsSortDirection;
  additive: boolean;
}

interface QdsResizeEventDetail {
  component: HTMLElement;
  key: string;
  width: number;
}

export class QdsDataTable extends HTMLElement {
  // ── Properties (not reflected to attributes) ────────────────────────
  private _columns: QdsColumnDef[] = [];
  private _rows: QdsTableRow[] = [];
  private _actions: QdsRowAction[] = [];
  private _sort: QdsSortState[] = [];
  private _selectedIds: string[] = [];

  // ── Attributes ──────────────────────────────────────────────────────
  private _density: QdsDensity = 'md';
  private _striped: boolean = false;
  private _stickyHeader: boolean = false;
  private _selectable: boolean = false;
  private _selectionMode: QdsSelectionMode = 'multiple';
  private _sortMode: QdsSortMode = 'client';
  private _paginate: boolean = false;
  private _page: number = 1;
  private _pageSize: number = 25;
  private _total: number = 0;
  private _totalExplicit: boolean = false;
  private _paginationMode: QdsPaginationMode = 'client';
  private _rowIdKey: string = 'id';
  private _rowLabelKey: string = '';
  private _emptyTitle: string = '';
  private _emptyDescription: string = '';
  private _loading: boolean = false;
  private _caption: string = '';

  // ── Internal state ──────────────────────────────────────────────────
  private _focusedRowIndex: number = -1;
  private _lastClickedRowIndex: number = -1;
  private _formatters: Map<string, Intl.NumberFormat | Intl.DateTimeFormat> = new Map();
  private _collator: Intl.Collator | null = null;
  private _announcement: string = '';
  private _cleanups: Array<() => void> = [];
  private _connected: boolean = false;

  static get observedAttributes(): string[] {
    return [
      'density',
      'striped',
      'sticky-header',
      'selectable',
      'selection-mode',
      'selected-ids',
      'sort-mode',
      'paginate',
      'page',
      'page-size',
      'total',
      'pagination-mode',
      'row-id-key',
      'row-label-key',
      'empty-title',
      'empty-description',
      'loading',
      'caption',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
  }

  // ── Properties ──────────────────────────────────────────────────────

  get columns(): QdsColumnDef[] {
    return this._columns;
  }

  set columns(value: QdsColumnDef[]) {
    this._columns = Array.isArray(value) ? value : [];
    this._formatters.clear();
    if (this._connected) this.render();
  }

  get rows(): QdsTableRow[] {
    return this._rows;
  }

  set rows(value: QdsTableRow[]) {
    this._rows = Array.isArray(value) ? value : [];
    if (this._connected) this.render();
  }

  get actions(): QdsRowAction[] {
    return this._actions;
  }

  set actions(value: QdsRowAction[]) {
    this._actions = Array.isArray(value) ? value : [];
    if (this._connected) this.render();
  }

  get sort(): QdsSortState[] {
    return this._sort;
  }

  set sort(value: QdsSortState[]) {
    this._sort = Array.isArray(value) ? value : [];
    if (this._connected) {
      if (this._sortMode === 'client') {
        this.render();
      } else {
        this._updateSortHeaders();
      }
    }
  }

  get selectedIds(): string[] {
    return this._selectedIds;
  }

  set selectedIds(value: string[]) {
    this._selectedIds = Array.isArray(value) ? value : [];
    if (this._connected) this._updateSelectionVisuals();
  }

  get density(): QdsDensity {
    return this._density;
  }

  set density(value: QdsDensity) {
    this._density = value === 'sm' || value === 'lg' ? value : 'md';
    this.setAttribute('density', this._density);
    if (this._connected) this.render();
  }

  get striped(): boolean {
    return this._striped;
  }

  set striped(value: boolean) {
    this._striped = Boolean(value);
    if (this._striped) this.setAttribute('striped', '');
    else this.removeAttribute('striped');
    if (this._connected) this.render();
  }

  get loading(): boolean {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = Boolean(value);
    if (this._loading) this.setAttribute('loading', '');
    else this.removeAttribute('loading');
    if (this._connected) this.render();
  }

  get page(): number {
    return this._page;
  }

  set page(value: number) {
    this._page = Math.max(1, Number(value) || 1);
    this.setAttribute('page', String(this._page));
    if (this._connected) this.render();
  }

  get pageSize(): number {
    return this._pageSize;
  }

  set pageSize(value: number) {
    this._pageSize = Math.max(1, Number(value) || 25);
    this.setAttribute('page-size', String(this._pageSize));
    if (this._connected) this.render();
  }

  get total(): number {
    return this._getEffectiveTotal();
  }

  set total(value: number) {
    this._total = Number(value) || 0;
    this._totalExplicit = true;
    this.setAttribute('total', String(this._total));
    if (this._connected) this.render();
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;
    upgradeProperty(this, 'columns');
    upgradeProperty(this, 'rows');
    upgradeProperty(this, 'actions');
    upgradeProperty(this, 'sort');
    upgradeProperty(this, 'selectedIds');
    this._connected = true;
    this.render();
  }

  disconnectedCallback(): void {
    this._cleanupListeners();
    this._connected = false;
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'density':
        this._density = newValue === 'sm' || newValue === 'lg' ? newValue : 'md';
        break;
      case 'striped':
        this._striped = newValue !== null;
        break;
      case 'sticky-header':
        this._stickyHeader = newValue !== null;
        break;
      case 'selectable':
        this._selectable = newValue !== null;
        break;
      case 'selection-mode':
        this._selectionMode = newValue === 'single' ? 'single' : 'multiple';
        break;
      case 'selected-ids':
        if (newValue !== null) {
          this._selectedIds = newValue
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        } else {
          this._selectedIds = [];
        }
        break;
      case 'sort-mode':
        this._sortMode = newValue === 'server' ? 'server' : 'client';
        break;
      case 'paginate':
        this._paginate = newValue !== null;
        break;
      case 'page':
        this._page = Math.max(1, parseInt(newValue || '1', 10) || 1);
        break;
      case 'page-size':
        this._pageSize = Math.max(1, parseInt(newValue || '25', 10) || 25);
        break;
      case 'total':
        if (newValue !== null) {
          this._total = parseInt(newValue, 10) || 0;
          this._totalExplicit = true;
        } else {
          this._totalExplicit = false;
        }
        break;
      case 'pagination-mode':
        this._paginationMode = newValue === 'server' ? 'server' : 'client';
        break;
      case 'row-id-key':
        this._rowIdKey = newValue || 'id';
        break;
      case 'row-label-key':
        this._rowLabelKey = newValue || '';
        break;
      case 'empty-title':
        this._emptyTitle = newValue || '';
        break;
      case 'empty-description':
        this._emptyDescription = newValue || '';
        break;
      case 'loading':
        this._loading = newValue !== null;
        break;
      case 'caption':
        this._caption = newValue || '';
        break;
    }

    if (this._connected) this.render();
  }

  // ── Rendering ───────────────────────────────────────────────────────

  private render(): void {
    if (!this.shadowRoot) return;

    this._cleanupListeners();

    const visibleCols = this._getVisibleColumns();
    const displayRows = this._getDisplayRows();
    const hasFixedLayout = this._hasFixedLayout(visibleCols);
    const effectiveTotal = this._getEffectiveTotal();
    const colCount =
      (this._selectable ? 1 : 0) +
      visibleCols.length +
      (this._actions.length > 0 ? 1 : 0);

    // Reset focused row if out of bounds
    if (this._focusedRowIndex >= displayRows.length) {
      this._focusedRowIndex = displayRows.length > 0 ? 0 : -1;
    }

    this.shadowRoot.innerHTML = `
      <style>${this._getStyles()}</style>
      <div part="container">
        <div part="toolbar"><slot name="toolbar"></slot></div>
        <div part="scroll">
          <table part="table"
            ${this._loading ? 'aria-busy="true"' : ''}
            ${this._paginate ? `aria-rowcount="${effectiveTotal}"` : ''}
            style="table-layout: ${hasFixedLayout ? 'fixed' : 'auto'};">
            ${this._caption ? `<caption class="visually-hidden">${this._escapeHtml(this._caption)}</caption>` : ''}
            ${this._buildColgroup(visibleCols)}
            ${this._buildThead(visibleCols)}
            <tbody part="tbody"></tbody>
          </table>
        </div>
        <div part="footer">
          ${this._paginate ? '<qds-pagination part="pagination"></qds-pagination>' : ''}
          <slot name="footer"></slot>
        </div>
        <div class="sr-only" aria-live="polite">${this._escapeHtml(this._announcement)}</div>
      </div>
    `;

    const tbody = this.shadowRoot.querySelector('tbody');
    if (tbody) {
      if (this._loading) {
        this._renderLoading(tbody as HTMLElement, colCount);
      } else if (displayRows.length === 0) {
        this._renderEmpty(tbody as HTMLElement, colCount);
      } else {
        this._renderBody(
          tbody as HTMLElement,
          displayRows,
          visibleCols,
        );
      }
    }

    // Configure pagination
    if (this._paginate) {
      const pagination = this.shadowRoot.querySelector('qds-pagination') as HTMLElement & {
        page: number;
        pageSize: number;
        total: number;
      };
      if (pagination) {
        pagination.page = this._page;
        pagination.pageSize = this._pageSize;
        pagination.total = effectiveTotal;
      }
    }

    this._updateRowFocus();
    this._setupListeners();
  }

  private _getVisibleColumns(): QdsColumnDef[] {
    return this._columns.filter((c) => !c.hidden);
  }

  private _getDisplayRows(): QdsTableRow[] {
    let result = this._rows;

    if (this._sortMode === 'client' && this._sort.length > 0) {
      result = this._clientSort(result);
    }

    if (this._paginate && this._paginationMode === 'client') {
      const start = (this._page - 1) * this._pageSize;
      result = result.slice(start, start + this._pageSize);
    }

    return result;
  }

  private _getEffectiveTotal(): number {
    return this._totalExplicit ? this._total : this._rows.length;
  }

  private _hasFixedLayout(visibleCols: QdsColumnDef[]): boolean {
    return visibleCols.some((c) => c.width);
  }

  private _buildColgroup(visibleCols: QdsColumnDef[]): string {
    let html = '<colgroup>';
    if (this._selectable) {
      html += '<col style="width: 44px;">';
    }
    for (const col of visibleCols) {
      const styleParts: string[] = [];
      if (col.width) styleParts.push(`width: ${this._escapeAttr(col.width)}`);
      // A column with neither width nor minWidth would otherwise be
      // squeezed toward zero under table-layout: fixed on narrow
      // viewports — the scroll container should absorb overflow instead.
      styleParts.push(`min-width: ${col.minWidth ? this._escapeAttr(col.minWidth) : '96px'}`);
      html += `<col data-key="${this._escapeAttr(col.key)}" style="${styleParts.join('; ')};">`;
    }
    if (this._actions.length > 0) {
      html += '<col style="width: 48px;">';
    }
    html += '</colgroup>';
    return html;
  }

  private _buildThead(visibleCols: QdsColumnDef[]): string {
    let html = '<thead part="thead"><tr part="header-row">';

    // Select-all header cell
    if (this._selectable) {
      const state = this._getSelectAllState();
      const checkedAttr = state.checked ? 'checked' : '';
      const indeterminateAttr = state.indeterminate ? 'indeterminate' : '';
      html += `<th scope="col" part="header-cell select-cell" style="width: 44px;">`;
      html += `<qds-checkbox ${checkedAttr} ${indeterminateAttr} size="sm" aria-label="Select all rows on this page"></qds-checkbox>`;
      html += `</th>`;
    }

    // Column headers
    for (const col of visibleCols) {
      const sortEntry = this._sort.findIndex((s) => s.key === col.key);
      const sortDir = sortEntry >= 0 ? this._sort[sortEntry].direction || '' : '';
      const sortPriority =
        sortEntry >= 0 && this._sort.length > 1 ? String(sortEntry + 1) : '';
      const ariaSort =
        sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none';
      const align = this._getColumnAlign(col);

      let stickyClass = '';
      let stickyStyle = '';
      let stickyAttr = '';
      if (col.sticky === 'start') {
        stickyClass = ' sticky-start';
        stickyAttr = ' sticky="start"';
        stickyStyle = ` style="left: ${this._getStickyLeft(col, visibleCols)};"`;
      } else if (col.sticky === 'end') {
        stickyClass = ' sticky-end';
        stickyAttr = ' sticky="end"';
        stickyStyle = ` style="right: ${this._getStickyRight(col, visibleCols)};"`;
      }

      html += `<th scope="col" part="header-cell" data-key="${this._escapeAttr(col.key)}" aria-sort="${ariaSort}" class="${stickyClass}"${stickyStyle}>`;
      html += `<qds-column-header`;
      html += ` key="${this._escapeAttr(col.key)}"`;
      html += ` label="${this._escapeAttr(col.label)}"`;
      html += ` align="${align}"`;
      if (col.sortable) html += ' sortable';
      if (sortDir) html += ` sort-direction="${sortDir}"`;
      if (sortPriority) html += ` sort-priority="${sortPriority}"`;
      if (stickyAttr) html += stickyAttr;
      if (col.ariaLabel)
        html += ` aria-label-text="${this._escapeAttr(col.ariaLabel)}"`;
      html += `></qds-column-header>`;
      html += '</th>';
    }

    // Actions header cell
    if (this._actions.length > 0) {
      html += '<th scope="col" part="header-cell actions-cell" style="width: 48px;"></th>';
    }

    html += '</tr></thead>';
    return html;
  }

  private _renderBody(
    tbody: HTMLElement,
    rows: QdsTableRow[],
    visibleCols: QdsColumnDef[],
  ): void {
    const fragment = document.createDocumentFragment();
    const startRow =
      this._paginate && this._paginationMode === 'client'
        ? (this._page - 1) * this._pageSize
        : 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const tr = this._buildBodyRow(row, i, visibleCols);

      // aria-rowindex when paginating
      if (this._paginate) {
        tr.setAttribute('aria-rowindex', String(startRow + i + 1));
      }

      fragment.appendChild(tr);
    }

    tbody.appendChild(fragment);
  }

  private _buildBodyRow(
    row: QdsTableRow,
    rowIndex: number,
    visibleCols: QdsColumnDef[],
  ): HTMLTableRowElement {
    const tr = document.createElement('tr');
    const rowId = this._getRowId(row);

    tr.setAttribute('part', 'row');
    tr.setAttribute('tabindex', '-1');
    tr.setAttribute('data-row-index', String(rowIndex));
    tr.setAttribute('data-row-id', rowId);

    // Selection checkbox cell
    if (this._selectable) {
      const td = document.createElement('td');
      td.setAttribute('part', 'select-cell');
      td.style.width = '44px';
      const checkbox = document.createElement('qds-checkbox');
      checkbox.setAttribute('size', 'sm');
      checkbox.setAttribute('aria-label', `Select ${this._getRowLabel(row)}`);
      if (this._selectedIds.includes(rowId)) {
        checkbox.setAttribute('checked', '');
      }
      td.appendChild(checkbox);
      tr.appendChild(td);
    }

    // Data cells
    let isFirstDataCell = true;
    for (const col of visibleCols) {
      const td = document.createElement('td');
      const isNumeric = this._isNumericColumn(col);
      const align = this._getColumnAlign(col);

      const parts = ['cell'];
      if (isNumeric) parts.push('cell-numeric');
      td.setAttribute('part', parts.join(' '));

      if (isFirstDataCell) {
        td.setAttribute('scope', 'row');
        isFirstDataCell = false;
      }

      // Alignment
      if (align === 'end') {
        td.style.textAlign = 'right';
      } else if (align === 'center') {
        td.style.textAlign = 'center';
      }

      // Sticky column styling
      if (col.sticky === 'start') {
        td.classList.add('sticky-start');
        td.style.left = this._getStickyLeft(col, visibleCols);
      } else if (col.sticky === 'end') {
        td.classList.add('sticky-end');
        td.style.right = this._getStickyRight(col, visibleCols);
      }

      // Cell content — NEVER innerHTML
      this._populateCellContent(td, col, row);

      tr.appendChild(td);
    }

    // Actions cell
    if (this._actions.length > 0) {
      const td = document.createElement('td');
      td.setAttribute('part', 'actions-cell');
      td.style.width = '48px';
      const rowActions = document.createElement('qds-row-actions');
      rowActions.setAttribute('row-id', rowId);
      rowActions.setAttribute('size', this._density);
      // Set properties after element is connected
      const actionsEl = rowActions as unknown as {
        actions: QdsRowAction[];
        row: QdsTableRow;
      };
      actionsEl.actions = this._actions;
      actionsEl.row = row;
      td.appendChild(rowActions);
      tr.appendChild(td);
    }

    // Selected state
    if (this._selectedIds.includes(rowId)) {
      tr.classList.add('selected');
      tr.setAttribute('part', 'row row-selected');
    }

    return tr;
  }

  private _populateCellContent(
    td: HTMLTableCellElement,
    col: QdsColumnDef,
    row: QdsTableRow,
  ): void {
    const value = row[col.key];

    // 1. render() function — Node appended, string set as textContent
    if (col.render) {
      const result = col.render(value, row);
      if (result instanceof Node) {
        td.appendChild(result);
      } else {
        td.textContent = String(result);
      }
      return;
    }

    // 2. format() function — string return set as textContent
    if (col.format) {
      td.textContent = col.format(value, row);
      return;
    }

    // 3. Built-in link — the cell value is the href, textKey supplies its label.
    if (col.type === 'link') {
      const href = sanitizeUrl(value);
      const textValue = col.textKey ? row[col.textKey] : value;
      const text = textValue == null ? '' : String(textValue);

      // An unsafe or empty URL is displayed as text, never emitted as a link.
      if (!href) {
        td.textContent = text;
        return;
      }

      const link = document.createElement('a');
      link.className = 'qds-table-link';
      link.href = href;
      link.textContent = text;
      const target = col.target || '_self';
      link.target = target;
      if (target === '_blank') {
        link.rel = 'noopener noreferrer';
      }
      td.appendChild(link);
      return;
    }

    // 4. Type-based formatting
    if (value == null) {
      td.textContent = '';
      return;
    }

    const type = col.type || 'text';
    if (type === 'number' || type === 'currency' || type === 'percent') {
      const formatter = this._getFormatter(col) as Intl.NumberFormat;
      td.textContent = formatter.format(value as number);
    } else if (type === 'date') {
      const formatter = this._getFormatter(col) as Intl.DateTimeFormat;
      const date = value instanceof Date ? value : new Date(value as string);
      td.textContent = formatter.format(date);
    } else {
      // text verbatim — NEVER innerHTML
      td.textContent = String(value);
    }
  }

  private _renderEmpty(tbody: HTMLElement, colCount: number): void {
    const hasEmptySlot = this.querySelector('[slot="empty"]') !== null;

    if (hasEmptySlot) {
      tbody.innerHTML = `<tr part="empty"><td colspan="${colCount}"><slot name="empty"></slot></td></tr>`;
    } else {
      const emptyState = document.createElement('qds-empty-state');
      emptyState.setAttribute('variant', 'empty');
      if (this._emptyTitle) {
        emptyState.setAttribute('title-text', this._emptyTitle);
      }
      if (this._emptyDescription) {
        emptyState.setAttribute('description', this._emptyDescription);
      }
      const tr = document.createElement('tr');
      tr.setAttribute('part', 'empty');
      const td = document.createElement('td');
      td.setAttribute('colspan', String(colCount));
      td.style.textAlign = 'center';
      td.appendChild(emptyState);
      tr.appendChild(td);
      tbody.appendChild(tr);
    }
  }

  private _renderLoading(tbody: HTMLElement, colCount: number): void {
    const skeletonCount = Math.min(this._pageSize, 5);
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < skeletonCount; i++) {
      const tr = document.createElement('tr');
      tr.setAttribute('part', 'skeleton-row');
      tr.setAttribute('aria-hidden', 'true');
      for (let j = 0; j < colCount; j++) {
        const td = document.createElement('td');
        const skeleton = document.createElement('div');
        skeleton.className = 'skeleton-cell';
        td.appendChild(skeleton);
        tr.appendChild(td);
      }
      fragment.appendChild(tr);
    }

    tbody.appendChild(fragment);
    this._announce('Loading');
  }

  // ── Formatters ──────────────────────────────────────────────────────

  private _getFormatter(col: QdsColumnDef): Intl.NumberFormat | Intl.DateTimeFormat {
    const key = col.key;
    const existing = this._formatters.get(key);
    if (existing) return existing;

    const type = col.type || 'text';
    let formatter: Intl.NumberFormat | Intl.DateTimeFormat;

    if (type === 'currency') {
      formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
      });
    } else if (type === 'percent') {
      formatter = new Intl.NumberFormat(undefined, {
        style: 'percent',
        maximumFractionDigits: 2,
      });
    } else if (type === 'number') {
      formatter = new Intl.NumberFormat();
    } else if (type === 'date') {
      formatter = new Intl.DateTimeFormat();
    } else {
      formatter = new Intl.NumberFormat();
    }

    this._formatters.set(key, formatter);
    return formatter;
  }

  // ── Column helpers ──────────────────────────────────────────────────

  private _getColumnAlign(col: QdsColumnDef): 'start' | 'center' | 'end' {
    if (col.align) return col.align;
    const type = col.type || 'text';
    if (type === 'number' || type === 'currency' || type === 'percent') {
      return 'end';
    }
    return 'start';
  }

  private _isNumericColumn(col: QdsColumnDef): boolean {
    const type = col.type || 'text';
    return type === 'number' || type === 'currency' || type === 'percent';
  }

  // ── Row helpers ─────────────────────────────────────────────────────

  private _getRowId(row: QdsTableRow): string {
    return String(row[this._rowIdKey] ?? '');
  }

  private _getRowLabel(row: QdsTableRow): string {
    const labelKey = this._getEffectiveRowLabelKey();
    return String(row[labelKey] ?? this._getRowId(row));
  }

  private _getEffectiveRowLabelKey(): string {
    if (this._rowLabelKey) return this._rowLabelKey;
    const visibleCols = this._getVisibleColumns();
    const firstTextCol = visibleCols.find(
      (c) => !c.type || c.type === 'text',
    );
    return firstTextCol ? firstTextCol.key : this._rowIdKey;
  }

  // ── Sort ────────────────────────────────────────────────────────────

  private _clientSort(rows: QdsTableRow[]): QdsTableRow[] {
    if (this._sort.length === 0) return rows;

    const result = [...rows];

    // Apply sorts in reverse priority order for stable multi-sort
    for (let i = this._sort.length - 1; i >= 0; i--) {
      const state = this._sort[i];
      if (!state.key || !state.direction) continue;

      const col = this._columns.find((c) => c.key === state.key);
      if (!col) continue;

      const dir = state.direction === 'asc' ? 1 : -1;
      const type = col.type || 'text';

      result.sort((a, b) => {
        const av = a[state.key!];
        const bv = b[state.key!];

        // null/undefined sort last in both directions
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;

        if (type === 'number' || type === 'currency' || type === 'percent') {
          return ((av as number) - (bv as number)) * dir;
        } else if (type === 'date') {
          return String(av).localeCompare(String(bv)) * dir;
        } else {
          // Use Intl.Collator — an order of magnitude faster than localeCompare
          if (!this._collator) {
            this._collator = new Intl.Collator(undefined, { numeric: true });
          }
          return this._collator.compare(String(av), String(bv)) * dir;
        }
      });
    }

    return result;
  }

  private _handleSortEvent(e: Event): void {
    const detail = (e as CustomEvent<QdsSortEventDetail>).detail;
    if (!detail || !detail.key) return;

    const { key, direction, additive } = detail;

    if (additive) {
      // Multi-sort: find existing entry for this key
      const existing = this._sort.findIndex((s) => s.key === key);
      if (existing >= 0) {
        if (direction === null) {
          // Remove from sort
          this._sort = this._sort.filter((_, i) => i !== existing);
        } else {
          // Update direction
          this._sort = this._sort.map((s, i) =>
            i === existing ? { key, direction } : s,
          );
        }
      } else {
        // Add new sort entry
        this._sort = [...this._sort, { key, direction }];
      }
    } else {
      // Single sort: replace
      this._sort = direction === null ? [] : [{ key, direction }];
    }

    // Update headers (aria-sort, sort-direction, sort-priority)
    this._updateSortHeaders();

    // Announce
    this._announceSort();

    // Fire qds-sort-change
    this.dispatchEvent(
      new CustomEvent('qds-sort-change', {
        bubbles: true,
        composed: true,
        detail: { component: this, sort: [...this._sort] },
      }),
    );

    // In client mode, re-render with sorted rows
    if (this._sortMode === 'client') {
      this.render();
    }
  }

  private _updateSortHeaders(): void {
    const visibleCols = this._getVisibleColumns();
    for (const col of visibleCols) {
      const th = this.shadowRoot?.querySelector(
        `th[data-key="${this._escapeAttr(col.key)}"]`,
      );
      if (!th) continue;

      const sortEntry = this._sort.findIndex((s) => s.key === col.key);
      const header = th.querySelector('qds-column-header');

      if (sortEntry >= 0) {
        const state = this._sort[sortEntry];
        const dir = state.direction || '';
        if (header) {
          if (dir) {
            header.setAttribute('sort-direction', dir);
          } else {
            header.removeAttribute('sort-direction');
          }
          if (this._sort.length > 1) {
            header.setAttribute('sort-priority', String(sortEntry + 1));
          } else {
            header.removeAttribute('sort-priority');
          }
        }
        th.setAttribute(
          'aria-sort',
          state.direction === 'asc'
            ? 'ascending'
            : state.direction === 'desc'
              ? 'descending'
              : 'none',
        );
      } else {
        if (header) {
          header.removeAttribute('sort-direction');
          header.removeAttribute('sort-priority');
        }
        th.setAttribute('aria-sort', 'none');
      }
    }
  }

  // ── Selection ───────────────────────────────────────────────────────

  private _getSelectAllState(): { checked: boolean; indeterminate: boolean } {
    const pageRows = this._getDisplayRows();
    if (pageRows.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const pageIds = pageRows.map((r) => this._getRowId(r));
    const selectedCount = pageIds.filter((id) =>
      this._selectedIds.includes(id),
    ).length;

    if (selectedCount === 0) return { checked: false, indeterminate: false };
    if (selectedCount === pageIds.length) {
      return { checked: true, indeterminate: false };
    }
    return { checked: false, indeterminate: true };
  }

  private _handleSelectAll(checked: boolean): void {
    const pageRows = this._getDisplayRows();
    const pageIds = pageRows.map((r) => this._getRowId(r));

    if (checked) {
      const idSet = new Set(this._selectedIds);
      for (const id of pageIds) idSet.add(id);
      this._selectedIds = Array.from(idSet);
    } else {
      const pageIdSet = new Set(pageIds);
      this._selectedIds = this._selectedIds.filter((id) => !pageIdSet.has(id));
    }

    this._updateSelectionVisuals();
    this._dispatchSelectionChange();
  }

  private _handleRowSelection(
    rowId: string,
    checked: boolean,
    shiftKey: boolean,
    rowIndex: number,
  ): void {
    if (this._selectionMode === 'single') {
      this._selectedIds = checked ? [rowId] : [];
    } else {
      if (shiftKey && this._lastClickedRowIndex >= 0) {
        // Extend selection from last clicked to current, in visual order
        const start = Math.min(this._lastClickedRowIndex, rowIndex);
        const end = Math.max(this._lastClickedRowIndex, rowIndex);
        const pageRows = this._getDisplayRows();
        const idSet = new Set(this._selectedIds);
        for (let i = start; i <= end; i++) {
          const id = this._getRowId(pageRows[i]);
          if (checked) idSet.add(id);
          else idSet.delete(id);
        }
        this._selectedIds = Array.from(idSet);
      } else {
        if (checked) {
          if (!this._selectedIds.includes(rowId)) {
            this._selectedIds = [...this._selectedIds, rowId];
          }
        } else {
          this._selectedIds = this._selectedIds.filter((id) => id !== rowId);
        }
      }
    }

    this._lastClickedRowIndex = rowIndex;
    this._updateSelectionVisuals();

    const rows = this._getDisplayRows();
    const row = rows[rowIndex];
    this._dispatchSelectionChange(row);
  }

  private _updateSelectionVisuals(): void {
    const tbody = this.shadowRoot?.querySelector('tbody');
    if (tbody) {
      const trs = tbody.querySelectorAll('tr[part~="row"]');
      trs.forEach((tr) => {
        const rowId = tr.getAttribute('data-row-id') || '';
        const isSelected = this._selectedIds.includes(rowId);

        tr.classList.toggle('selected', isSelected);

        // Update part attribute
        const currentPart = tr.getAttribute('part') || '';
        const parts = currentPart
          .split(' ')
          .filter((p) => p !== 'row-selected');
        if (isSelected) parts.push('row-selected');
        tr.setAttribute('part', parts.join(' '));

        // Update per-row checkbox
        const checkbox = tr.querySelector('qds-checkbox') as unknown as {
          checked: boolean;
        } | null;
        if (checkbox) {
          checkbox.checked = isSelected;
        }
      });
    }

    // Update select-all checkbox
    const selectAll = this.shadowRoot?.querySelector(
      'thead qds-checkbox',
    ) as unknown as { checked: boolean; indeterminate: boolean } | null;
    if (selectAll) {
      const state = this._getSelectAllState();
      selectAll.checked = state.checked;
      selectAll.indeterminate = state.indeterminate;
    }

    this._announceSelection();
  }

  // ── Click handling ──────────────────────────────────────────────────

  private _handleTableClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target) return;

    // Checkbox click — handle selection, never fire row-click
    if (target.tagName === 'QDS-CHECKBOX') {
      const th = target.closest('th');
      const td = target.closest('td');

      if (th) {
        // Select-all checkbox
        this._handleSelectAll(
          (target as unknown as { checked: boolean }).checked,
        );
      } else if (td) {
        // Per-row checkbox
        const tr = td.closest('tr');
        if (!tr) return;
        const rowIndex = parseInt(tr.getAttribute('data-row-index') || '0', 10);
        const rows = this._getDisplayRows();
        if (rowIndex < 0 || rowIndex >= rows.length) return;

        const row = rows[rowIndex];
        const rowId = this._getRowId(row);
        const checked = (target as unknown as { checked: boolean }).checked;

        this._handleRowSelection(rowId, checked, e.shiftKey, rowIndex);
      }
      return;
    }

    // Row actions click — handled by qds-row-action event, not here
    if (target.tagName === 'QDS-ROW-ACTIONS') {
      return;
    }

    // Row body click — fire qds-row-click, do NOT change selection
    const tr = target.closest('tr');
    if (!tr) return;
    const part = tr.getAttribute('part') || '';
    if (!part.includes('row') || part.includes('header-row')) return;
    if (part.includes('skeleton-row') || part.includes('empty')) return;

    const rowIndex = parseInt(tr.getAttribute('data-row-index') || '0', 10);
    const rows = this._getDisplayRows();
    if (rowIndex < 0 || rowIndex >= rows.length) return;

    // Update focused row
    if (this._focusedRowIndex !== rowIndex) {
      this._focusedRowIndex = rowIndex;
      this._updateRowFocus();
    }

    const row = rows[rowIndex];
    const rowId = this._getRowId(row);
    this._dispatchRowClick(rowId, row);
  }

  // ── Keyboard ────────────────────────────────────────────────────────

  private _handleKeyDown(e: KeyboardEvent): void {
    const rows = this._getDisplayRows();
    if (rows.length === 0) return;

    let newIndex = this._focusedRowIndex;
    let handled = true;

    switch (e.key) {
      case 'ArrowDown':
        newIndex = Math.min(this._focusedRowIndex + 1, rows.length - 1);
        break;
      case 'ArrowUp':
        newIndex = Math.max(this._focusedRowIndex - 1, 0);
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = rows.length - 1;
        break;
      case 'PageDown':
        newIndex = Math.min(
          this._focusedRowIndex + this._getViewportRowCount(),
          rows.length - 1,
        );
        break;
      case 'PageUp':
        newIndex = Math.max(
          this._focusedRowIndex - this._getViewportRowCount(),
          0,
        );
        break;
      case ' ':
        if (this._selectable && this._focusedRowIndex >= 0) {
          const row = rows[this._focusedRowIndex];
          const rowId = this._getRowId(row);
          const isChecked = this._selectedIds.includes(rowId);
          this._handleRowSelection(rowId, !isChecked, false, this._focusedRowIndex);
        }
        break;
      case 'Enter':
        if (this._focusedRowIndex >= 0) {
          const row = rows[this._focusedRowIndex];
          const rowId = this._getRowId(row);
          this._dispatchRowClick(rowId, row);
        }
        break;
      default:
        handled = false;
    }

    if (handled) {
      e.preventDefault();
    }

    if (newIndex !== this._focusedRowIndex && newIndex >= 0) {
      this._focusedRowIndex = newIndex;
      this._updateRowFocus();
      this._scrollRowIntoView(newIndex);
    }
  }

  private _updateRowFocus(): void {
    const tbody = this.shadowRoot?.querySelector('tbody');
    if (!tbody) return;

    const trs = tbody.querySelectorAll('tr[part~="row"]');
    if (this._focusedRowIndex < 0 && trs.length > 0) {
      this._focusedRowIndex = 0;
    }
    if (this._focusedRowIndex >= trs.length) {
      this._focusedRowIndex = trs.length > 0 ? trs.length - 1 : -1;
    }

    trs.forEach((tr, i) => {
      tr.setAttribute('tabindex', i === this._focusedRowIndex ? '0' : '-1');

      const currentPart = tr.getAttribute('part') || 'row';
      const parts = currentPart.split(' ').filter((p) => p !== 'row-focused');
      if (i === this._focusedRowIndex) {
        parts.push('row-focused');
      }
      tr.setAttribute('part', parts.join(' '));
      tr.classList.toggle('focused', i === this._focusedRowIndex);
    });
  }

  private _scrollRowIntoView(index: number): void {
    const scrollContainer = this.shadowRoot?.querySelector(
      '[part="scroll"]',
    ) as HTMLElement | null;
    if (!scrollContainer) return;

    const rowHeight =
      this._density === 'sm' ? 32 : this._density === 'lg' ? 48 : 40;
    const viewportHeight = scrollContainer.clientHeight;
    const scrollTop = scrollContainer.scrollTop;

    const rowTop = index * rowHeight;
    const rowBottom = rowTop + rowHeight;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + viewportHeight;

    if (rowTop < viewportTop) {
      scrollContainer.scrollTop = rowTop;
    } else if (rowBottom > viewportBottom) {
      scrollContainer.scrollTop = rowBottom - viewportHeight;
    }
  }

  private _getViewportRowCount(): number {
    const scrollContainer = this.shadowRoot?.querySelector(
      '[part="scroll"]',
    ) as HTMLElement | null;
    if (!scrollContainer || !scrollContainer.clientHeight) return 10;
    const rowHeight =
      this._density === 'sm' ? 32 : this._density === 'lg' ? 48 : 40;
    return Math.floor(scrollContainer.clientHeight / rowHeight);
  }

  // ── Event relay ─────────────────────────────────────────────────────

  private _handleRowActionEvent(e: Event): void {
    const detail = (e as CustomEvent).detail;
    if (!detail) return;

    // Stop the original from reaching the consumer
    e.stopPropagation();

    // Re-dispatch with component set to the data-table
    const relayDetail: QdsRowActionEventDetail = {
      component: this,
      action: detail.action,
      rowId: detail.rowId,
      row: detail.row,
    };

    this.dispatchEvent(
      new CustomEvent('qds-row-action', {
        bubbles: true,
        composed: true,
        detail: relayDetail,
      }),
    );
  }

  private _handlePageChangeEvent(e: Event): void {
    const detail = (e as CustomEvent).detail;
    if (!detail) return;

    e.stopPropagation();

    this._page = detail.page;
    this._pageSize = detail.pageSize;

    // In client mode, re-render with the new page slice
    if (this._paginationMode === 'client') {
      this.render();
    }

    const relayDetail: QdsPageChangeEventDetail = {
      component: this,
      page: detail.page,
      pageSize: detail.pageSize,
      total: detail.total ?? this._getEffectiveTotal(),
    };

    this.dispatchEvent(
      new CustomEvent('qds-page-change', {
        bubbles: true,
        composed: true,
        detail: relayDetail,
      }),
    );
  }

  private _handleResizeEvent(e: Event): void {
    const detail = (e as CustomEvent<QdsResizeEventDetail>).detail;
    if (!detail || !detail.key) return;

    // Update the col width
    const col = this.shadowRoot?.querySelector(
      `col[data-key="${this._escapeAttr(detail.key)}"]`,
    ) as HTMLElement | null;
    if (col) {
      col.style.width = `${detail.width}px`;
    }

    // Fire qds-column-resize on resize-end
    if (e.type === 'qds-resize-end') {
      this.dispatchEvent(
        new CustomEvent('qds-column-resize', {
          bubbles: true,
          composed: true,
          detail: {
            component: this,
            key: detail.key,
            width: detail.width,
          },
        }),
      );
    }
  }

  // ── Event dispatch ──────────────────────────────────────────────────

  private _dispatchRowClick(rowId: string, row: QdsTableRow): void {
    this.dispatchEvent(
      new CustomEvent('qds-row-click', {
        bubbles: true,
        composed: true,
        detail: { component: this, rowId, row },
      }),
    );
  }

  private _dispatchSelectionChange(row?: QdsTableRow): void {
    const detail: QdsSelectionChangeEventDetail = {
      component: this,
      selectedIds: [...this._selectedIds],
      row,
    };

    this.dispatchEvent(
      new CustomEvent('qds-selection-change', {
        bubbles: true,
        composed: true,
        detail,
      }),
    );
  }

  // ── Announcements ───────────────────────────────────────────────────

  private _announce(message: string): void {
    this._announcement = message;
    const liveRegion = this.shadowRoot?.querySelector('.sr-only');
    if (liveRegion) {
      liveRegion.textContent = message;
    }
  }

  private _announceSort(): void {
    if (this._sort.length === 0) {
      this._announce('Sort cleared');
      return;
    }
    const first = this._sort[0];
    if (!first.key || !first.direction) {
      this._announce('Sort cleared');
      return;
    }
    const col = this._columns.find((c) => c.key === first.key);
    const label = col?.label || first.key;
    const dir = first.direction === 'asc' ? 'ascending' : 'descending';
    this._announce(`Sorted by ${label}, ${dir}`);
  }

  private _announceSelection(): void {
    const count = this._selectedIds.length;
    if (count === 0) {
      this._announce('No rows selected');
    } else {
      this._announce(`${count} row${count === 1 ? '' : 's'} selected`);
    }
  }

  // ── Sticky column offsets ───────────────────────────────────────────

  private _getStickyLeft(col: QdsColumnDef, visibleCols: QdsColumnDef[]): string {
    let offset = 0;
    if (this._selectable) offset += 44;

    for (const c of visibleCols) {
      if (c.key === col.key) break;
      if (c.sticky === 'start') {
        offset += this._parsePixelWidth(c.width);
      }
    }

    return `${offset}px`;
  }

  private _getStickyRight(col: QdsColumnDef, visibleCols: QdsColumnDef[]): string {
    let offset = 0;
    if (this._actions.length > 0) offset += 48;

    for (let i = visibleCols.length - 1; i >= 0; i--) {
      if (visibleCols[i].key === col.key) break;
      if (visibleCols[i].sticky === 'end') {
        offset += this._parsePixelWidth(visibleCols[i].width);
      }
    }

    return `${offset}px`;
  }

  private _parsePixelWidth(width: string | undefined): number {
    if (!width) return 100;
    const match = width.match(/^(\d+(?:\.\d+)?)px$/);
    return match ? parseFloat(match[1]) : 100;
  }

  // ── Escaping ────────────────────────────────────────────────────────

  private _escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private _escapeAttr(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ── Listeners ───────────────────────────────────────────────────────

  private _cleanupListeners(): void {
    for (const cleanup of this._cleanups) {
      cleanup();
    }
    this._cleanups = [];
  }

  private _setupListeners(): void {
    const table = this.shadowRoot?.querySelector('table');
    const pagination = this.shadowRoot?.querySelector('qds-pagination');

    if (table) {
      // Click handling (delegated — checkboxes, row body, etc.)
      const onClick = (e: MouseEvent) => this._handleTableClick(e);
      table.addEventListener('click', onClick);
      this._cleanups.push(() => table.removeEventListener('click', onClick));

      // Sort events from column headers
      const onSort = (e: Event) => this._handleSortEvent(e);
      table.addEventListener('qds-sort', onSort);
      this._cleanups.push(() => table.removeEventListener('qds-sort', onSort));

      // Keyboard navigation
      const onKeyDown = (e: KeyboardEvent) => this._handleKeyDown(e);
      table.addEventListener('keydown', onKeyDown);
      this._cleanups.push(() => table.removeEventListener('keydown', onKeyDown));

      // Row action events (delegated, composed)
      const onRowAction = (e: Event) => this._handleRowActionEvent(e);
      table.addEventListener('qds-row-action', onRowAction);
      this._cleanups.push(() =>
        table.removeEventListener('qds-row-action', onRowAction),
      );

      // Column resize events
      const onResize = (e: Event) => this._handleResizeEvent(e);
      table.addEventListener('qds-resize', onResize);
      table.addEventListener('qds-resize-end', onResize);
      this._cleanups.push(() => {
        table.removeEventListener('qds-resize', onResize);
        table.removeEventListener('qds-resize-end', onResize);
      });
    }

    if (pagination) {
      const onPageChange = (e: Event) => this._handlePageChangeEvent(e);
      pagination.addEventListener('qds-page-change', onPageChange);
      this._cleanups.push(() =>
        pagination.removeEventListener('qds-page-change', onPageChange),
      );
    }
  }

  // ── Styles ──────────────────────────────────────────────────────────

  private _getStyles(): string {
    return `
      :host {
        display: flex;
        flex-direction: column;
        font-family: var(--theme-font-family, sans-serif);
        color: var(--theme-text-primary, #333);
        --qds-row-height: var(--qds-control-height-md, 40px);
        --qds-row-font-size: 14px;
        --qds-row-padding: var(--theme-space-3, 12px);
      }

      :host([density="sm"]) {
        --qds-row-height: var(--qds-control-height-sm, 32px);
        --qds-row-font-size: 13px;
        --qds-row-padding: var(--theme-space-2, 8px);
      }

      :host([density="lg"]) {
        --qds-row-height: var(--qds-control-height-lg, 48px);
        --qds-row-font-size: 14px;
        --qds-row-padding: var(--theme-space-3, 12px);
      }

      [part="container"] {
        display: flex;
        flex-direction: column;
        width: 100%;
        /* :host is the flex container (so a consumer-set max-height on the
           host works via flex sizing, not percentage-height resolution,
           which does not reliably apply against an ancestor sized only by
           max-height). This element is host's flex item: flex: 1 1 auto
           lets it take the full available height, and min-height: 0 lets
           it shrink below its content size instead of refusing to (the
           flex-item default is min-height: auto). */
        flex: 1 1 auto;
        min-height: 0;
      }

      [part="toolbar"] {
        padding: var(--theme-space-2, 8px) var(--theme-space-3, 12px);
      }

      [part="scroll"] {
        overflow: auto;
        flex: 1 1 auto;
        /* Flex items default to min-height: auto, which refuses to shrink
           below content size — that silently defeats flex: 1 1 auto and a
           consumer-set max-height on the host: the row content just grows
           past the constraint instead of scrolling inside it. min-height: 0
           lets this region actually shrink to the space the host allows, so
           sticky-header has a real internal scroll container to attach to. */
        min-height: 0;
      }

      table {
        min-width: 100%;
        border-collapse: collapse;
        font-size: var(--qds-row-font-size);
        font-family: var(--theme-font-family, sans-serif);
      }

      caption.visually-hidden,
      .visually-hidden,
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      th, td {
        padding: 0 var(--qds-row-padding);
        height: var(--qds-row-height);
        box-sizing: border-box;
        text-align: left;
        vertical-align: middle;
        border-bottom: 1px solid var(--theme-border-primary, #E4E9F2);
      }

      th {
        background: var(--theme-background-tertiary, #e9ecef);
        border-bottom: 1px solid var(--theme-border-strong, #AFBBD1);
        font-weight: 600;
        color: var(--theme-text-secondary, #555);
        font-size: 13px;
        text-transform: none;
        padding-top: 0;
        padding-bottom: 0;
      }

      /* The table owns the single header separator. qds-column-header is also
         usable on its own, where it supplies a border, but that would create a
         second line inside every table header and become visibly discontinuous
         beneath sticky columns. */
      thead qds-column-header {
        --qds-column-header-border-color: transparent;
      }

      td {
        color: var(--theme-text-primary, #333);
      }

      .qds-table-link {
        color: var(--link-color, var(--theme-accent-primary, #445E89));
        font: inherit;
        text-decoration: none;
      }

      .qds-table-link:hover {
        color: var(--link-hover-color, var(--theme-accent-secondary, #2D4B7B));
        text-decoration: underline;
      }

      .qds-table-link:focus-visible {
        outline: 2px solid var(--theme-accent-primary, #445E89);
        outline-offset: 2px;
        border-radius: var(--theme-radius-sm, 4px);
      }

      /* No top border on first body row — header's bottom border serves */
      tbody tr:first-child td {
        border-top: none;
      }

      /* Numeric alignment */
      td[part~="cell-numeric"] {
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }

      /* Every row supplies an opaque base and an optional translucent state
         overlay. Paint both on every cell so sticky cells stay aligned, and so
         hover/selection layers add to striped rows rather than replacing them. */
      tbody tr[part~="row"] {
        --qds-row-base: var(--theme-background-primary, #fff);
        --qds-row-overlay: transparent;
      }

      tbody tr[part~="row"] > td {
        background: linear-gradient(var(--qds-row-overlay), var(--qds-row-overlay)), var(--qds-row-base);
      }

      /* Zebra striping — off by default */
      :host([striped]) tbody tr:nth-child(even) {
        --qds-row-base: var(--theme-background-tertiary, #e9ecef);
      }

      /* Row hover */
      tbody tr[part~="row"]:hover {
        --qds-row-overlay: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
      }

      /* Selected row */
      tbody tr.selected {
        --qds-row-overlay: var(--theme-background-accent-subtle, rgba(68, 94, 137, 0.08));
      }

      /* Selected + hover — selection wins, hover deepens slightly */
      tbody tr.selected:hover {
        --qds-row-overlay: var(--theme-background-accent-subtle, rgba(68, 94, 137, 0.14));
      }

      /* Focused row — a restrained Excel-style accent marker on its first cell.
         An outline on a table row does not paint consistently around sticky cells,
         which can leave broken or doubled rules between rows. */
      tbody tr.focused > td:first-child {
        box-shadow: inset 4px 0 0 var(--theme-accent-primary, #445E89);
      }

      /* Sticky header */
      :host([sticky-header]) thead th {
        position: sticky;
        top: 0;
        z-index: 2;
      }

      /* Sticky columns — body */
      td.sticky-start {
        position: sticky;
        z-index: 1;
      }

      td.sticky-end {
        position: sticky;
        z-index: 1;
      }

      /* Sticky columns — header */
      th.sticky-start {
        position: sticky;
        z-index: 1;
        background: var(--theme-background-tertiary, #e9ecef);
      }

      th.sticky-end {
        position: sticky;
        z-index: 1;
        background: var(--theme-background-tertiary, #e9ecef);
      }

      /* Intersection of sticky header and sticky column — higher z-index */
      :host([sticky-header]) thead th.sticky-start,
      :host([sticky-header]) thead th.sticky-end {
        z-index: 3;
      }

      /* Select cell */
      td[part~="select-cell"],
      th[part~="select-cell"] {
        width: 44px;
        text-align: center;
        vertical-align: middle;
      }

      td[part~="select-cell"] qds-checkbox,
      th[part~="select-cell"] qds-checkbox {
        display: inline-flex;
        vertical-align: middle;
      }

      /* Actions cell */
      td[part~="actions-cell"],
      th[part~="actions-cell"] {
        width: 48px;
        text-align: right;
        white-space: nowrap;
      }

      /* Empty state row */
      tbody tr[part~="empty"] td {
        border-bottom: none;
        padding: var(--theme-space-6, 24px);
      }

      /* Skeleton rows */
      tr[part~="skeleton-row"] td {
        border-bottom: 1px solid var(--theme-border-primary, #E4E9F2);
      }

      .skeleton-cell {
        height: 60%;
        min-height: 16px;
        width: 80%;
        background: linear-gradient(
          90deg,
          var(--theme-background-secondary, #F8F9FA) 25%,
          var(--theme-background-tertiary, #e9ecef) 50%,
          var(--theme-background-secondary, #F8F9FA) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-shimmer 1.5s infinite;
        border-radius: var(--theme-radius-sm, 4px);
      }

      @keyframes skeleton-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }

      /* Footer */
      [part="footer"] {
        border-top: 1px solid var(--theme-border-primary, #E4E9F2);
      }

      /* Coarse pointer — touch targets */
      @media (pointer: coarse) {
        :host {
          --qds-row-height: 44px;
        }
      }
    `;
  }
}

if (!customElements.get('qds-data-table')) {
  customElements.define('qds-data-table', QdsDataTable);
}
